import { Component, OnInit, Input, OnDestroy, NgZone } from '@angular/core';
import { Router } from "@angular/router";
import { interval, Subscription, of, Observable, BehaviorSubject } from "rxjs";
import { tap, catchError, switchMap, finalize, map} from "rxjs/operators";
import { DynamicDialogService } from "../../services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../models/dialog.model";
import { AuthService, LoginTokenResponse } from "../../../services/auth.service";
import { TimeoutConfig } from "../../../services/auth.config";
import { UserIdentity } from "../../../models/user-identity.model";

@Component({
    selector: 'idle-timeout',
    templateUrl: './idle-timeout.component.html',
    styleUrls: ['./idle-timeout.component.scss'],
    standalone: false
})
export class IdleTimeoutComponent implements OnInit, OnDestroy {
    config: TimeoutConfig;
    lastActivity: BehaviorSubject<Date> = new BehaviorSubject<Date>(null);
    refreshInterval: Observable<any>;
    refreshSubscription: Subscription;
    subscription: Subscription = new Subscription();
    warningTimeout: any;
    logoutTimeout: any;
    refreshActive: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    timersActive: boolean = false;

    warningDialog: Dialog = new Dialog("You are about to logged out due to inactivity.  Do you wish to continue?", [new DialogButton("Continue", true, () => { this.refreshSession(); }), new DialogButton("Logout", false, () => { this.logout(); })]);
    securityDialog: Dialog = new Dialog("The security tokens protecting your session may have been compromised.  Your session will be logged out.  Please log back in to continue your work.", [new DialogButton("OK", true, () => { this.logout(); })]);
   

    get tokenDurationInMilliseconds(): number {
        if (this.config == null) return 0;
        return this.config.idleTimeoutMinutes * 60 * 1000;
    }

    get refreshThreshold(): number {
        return 10 * 60 * 1000; //10 minutes before full expiration
    }

    constructor(private router: Router, private auth: AuthService, private dialogService: DynamicDialogService) {  
        let configObs = this.auth.getTimeoutConfig().pipe(
            catchError((error) => {
                console.error(error);
                return of(null);
            }),
            switchMap((data: TimeoutConfig) => {
                this.config = data;
                this.setupListeners();
                return interval(1000).pipe(
                    switchMap(() => {
                        return this.auth.loggedInUser$;
                    })
                )
            }),
            tap((user: UserIdentity) => {
                if (user.isLoggedIn && !this.timersActive) {
                    this.startTimers();
                }
                if (!user.isLoggedIn && this.timersActive) {
                    this.deactivateTimers(false);
                }
            })            
        ).subscribe(
            () => {   },
            (error) => { console.error(error); }
        );
        this.subscription.add(configObs);
    }

    ngOnInit() {
        this.lastActivity.next(new Date());
        this.refreshInterval = interval(60000).pipe(
            tap(() => {
                let expDate = new Date(this.auth.tokenExpirationDate);
                let now = new Date();
                let timeRemaining: number = +expDate - +now;
                if (timeRemaining < this.refreshThreshold && this.auth.User.isLoggedIn) {
                    this.refreshSession();
                }
               
            })
        );
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
        if (this.refreshSubscription && !this.refreshSubscription.closed) this.refreshSubscription.unsubscribe();
        removeEventListener('click', this.resetTimers);
        removeEventListener('keyup', this.resetTimers);
        removeEventListener('mouseover', this.resetTimers);
    }

    deactivateTimers(isRestart: boolean): void {
        if (this.warningTimeout) clearTimeout(this.warningTimeout);
        if (this.logoutTimeout) clearTimeout(this.logoutTimeout);
        this.timersActive = isRestart;
        if (this.timersActive == false && this.refreshSubscription) this.refreshSubscription.unsubscribe();
    }

    logout(): void {
        this.deactivateTimers(false);
        this.auth.forceLogout(true);
        this.router.navigate(["auth", "logout"]);
    }

    refreshSession(): void {
        if (this.refreshActive.value == false) {
            this.refreshActive.next(true);
            this.auth.refreshToken();
            let watchRefresh = this.auth.sessionRefresh
                .pipe()
                .subscribe(
                    (response: LoginTokenResponse) => {
                        this.warningDialog.toggleDialog(false);
                        this.dialogService.updateDialog(this.warningDialog);                        
                        this.refreshSubscription.unsubscribe();
                        this.resetTimers();
                        this.refreshActive.next(false);
                        watchRefresh.unsubscribe();
                        if (response == null) this.showCompromisedDialog();
                },
                (error) => { console.error(error); }
            );
            this.subscription.add(watchRefresh);
            
        } else {
            console.log("refreshSession called when session is already refreshing");
        }
        
    }

    resetTimers(event: any = null): void {
        this.lastActivity.next(new Date());
        if (this.timersActive) {
            this.deactivateTimers(true);
            this.startTimers();
           
        }

    }

    setupListeners(): void {
        addEventListener('click', x =>  this.resetTimers(this));
        addEventListener('keyup', x => this.resetTimers(this));
        addEventListener('mouseover', x => this.resetTimers(this));
    }

    showCompromisedDialog(): void {
        this.securityDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.securityDialog);
    }

    startTimers(): void {
        const logoutValue: number = this.config.idleTimeoutMinutes * 60 * 1000;
        const warningValue: number = logoutValue - (this.config.idleWarningMinutes * 60 * 1000);
        if (this.config.warnTimeout) {
            this.warningTimeout = setTimeout(() => {
                this.warningDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.warningDialog);
            }, warningValue);
        }
        if (this.config.allowExpiration) {
            this.logoutTimeout = setTimeout(() => {
                this.logout();
            }, logoutValue);
            if (this.refreshSubscription == null || this.refreshSubscription.closed) {
                this.refreshSubscription = this.refreshInterval.pipe(
                    catchError((err) => {
                        console.error(err);
                        return of(null)
                    })
                ).subscribe(() => { }, (error) => { console.error(error); });
            }
        }
        this.timersActive = true;
    }


    
}

